/* eslint-disable no-console */
/**
 * App.js
 * Created by calhosh on 8/28/2020.
 */
/* eslint react/no-multi-comp: 0*/
import React, {Fragment, Suspense, memo, useMemo, useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Route, Outlet, RouterProvider, createBrowserRouter, createRoutesFromElements} from 'react-router-dom';
import './globals.less';
import './app.less';
import Main from './Main/Main';
import LoaderContainer from './LoaderContainer';
import env from '../util/env';
import {ReactiveFrame} from '@ey/canvascoreservices';
import Utility from '../util/utilityfunctions';
import LoadingIndicator from './Common/LoadingIndicator/LoadingIndicator';
import Errors from './Common/Errors/Errors';
import {useIsDrawerMode} from '../util/Ares/customHooks';
import ErrorPage from './ErrorPage';
import {labels} from '../util/utils';
import ReactiveModal from './Common/Modal/ReactiveModal';
import {useFrameTitle, useMenuClick, useNav} from '../util/customHooks';
import {isEqual} from 'lodash';
import {CookieConsentModal, CookieSettings, adobeAnalytics} from '@ey/voltron-analytics';
import envConfig from '@ey/voltron-config';
import TopBarNav from './Common/TopBarNav/TopBarNav';
import ToastMessageContainer from './Common/ToastMessage/ToastMessageContainer';
import styled from 'styled-components';
import {pageViewArgs} from './Common/AdobeAnalytics/actionHelper';
import {pageName, siteSection} from './Common/AdobeAnalytics/constants';

const searchParams = new URLSearchParams(location.search);
const engagementId = searchParams.get('engagementId') || searchParams.get('engagementid');

const COOKIE_SETTINGS_PATH = `/cookiesettings?engagementId=${engagementId}`;
const adobeAnalyticsEnabled = envConfig.adobeAnalytics?.enabled;
const DryDock = React.lazy(() => import('./Main/DryDock'));
const SpursDryDock = React.lazy(() => import('./Main/SpursDrydock'));
const InvinciblesDryDock = React.lazy(() => import('./Main/InvinciblesDryDock/InvinciblesDryDock'));
const Forms = React.lazy(() => import('./Main/Forms/CanvasFormContainer'));
const FormPrintViewWrapper = React.lazy(() => import('./Main/Forms/PrintView/FormPrintWrapper'));
const IndependenceLoader = React.lazy(() => import('./Main/Independence/IndependenceContainer'));
const ProfileLoader = React.lazy(() => import('./Main/Profile/ProfileContainer'));
const SeahawksDrydock = React.lazy(() => import('./Main/SeahawksDryDock/SeahawksDrydock'));
const AresV1 = React.lazy(() => import('./Main/Ares/AresV1Container'));
const AresV2 = React.lazy(() => import('./Main/Ares/AresV2Container'));
const TaskView = React.lazy(() => import('./Main/Ares/Tasks/TaskView'));
const GuidedWorkflowV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/GuidedWorkflowV2Container'));
const ExtendedV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/ExtendedV2Container'));
const HelixConfiguration = React.lazy(() => import('./Main/Ares/Helix/ConfigurationContainer'));
const FitRedirectionContainer = React.lazy(() => import('./Main/Ares/FIT/FitRedirectionContainer'));
const TrackChangesV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/TrackChangesV2Container'));
const CompareResponsesV2Container = React.lazy(() =>
	import('./Main/Ares/Tasks/TaskView/V2/CompareResponsesV2Container')
);
const CompareResponsesV2DrillDownContainer = React.lazy(() =>
	import('./Main/Ares/Tasks/TaskView/V2/CompareResponsesV2DrillDownContainer')
);
const AttributesContainer = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/Attributes/AttributesContainer'));
const StandaloneContainer = React.lazy(() => import('./Main/Ares/Standalone/StandaloneContainer'));
const BodiesContainer = React.lazy(() => import('./Main/Ares/Standalone/Bodies/BodiesContainer'));

const Layout = memo(({hasNavigation = false, hasError = false}) => {
	if (env.allowDrydocks()) {
		console.log('Drydocks are allowed in this environment');
	}

	useMenuClick();

	useFrameTitle();

	useNav();

	const isDrawerMode = useIsDrawerMode();
	const isModalOpen = useSelector((store) => store.isModalOpen);

	const framesLabels = useMemo(() => {
		return {
			maxTabsLocked: labels.maxTabsLocked,
			unPinTab: labels.unPinTab,
			pinTab: labels.pinTab,
			openInNewTab: labels.openInNewTab,
			close: labels.close,
			open: labels.expand,
			minimize: labels.collapse
		};
	}, [labels]);

	return (
		<Fragment>
			{hasNavigation && !isDrawerMode && <TopBarNav />}
			{hasError && <Errors />}
			{!isModalOpen && (
				<StyledGuidedWorkflowBanners className="StyledGuidedWorkflowBanners">
					<ToastMessageContainer />
				</StyledGuidedWorkflowBanners>
			)}
			<Main>
				<ReactiveModal />
				<Suspense fallback={<LoadingIndicator styleName="SuspenseLoadingIndicator" show fullscreen />}>
					{!Utility.pageInIframe() && <ReactiveFrame labels={framesLabels} />}
					<Outlet />
				</Suspense>
			</Main>
		</Fragment>
	);
}, isEqual);

Layout.displayName = 'Layout';

const App = memo(({hasNavigation = false, hasError = false}) => {
	const dispatch = useDispatch();
	const displayCookieConsent = useSelector((state) => state.adobeAnalytics.promptForConsent);
	const userPreferences = useSelector((store) => {
		return store.userPreferences;
	});
	const [routes, setRoutes] = useState(null);

	const trackRoute = (component, pageName, siteSection, isHomePage = false, isErrorPage = false, locale = null) => {
		const TrackedComponent = adobeAnalytics.TrackPageView(
			component,
			pageViewArgs(pageName, siteSection, isHomePage, isErrorPage)
		);
		if (locale) {
			return <TrackedComponent locale={locale} />;
		}
		return <TrackedComponent />;
	};

	// Adobe Analytics
	useEffect(() => {
		if (adobeAnalyticsEnabled && userPreferences?.emailAddress) {
			dispatch(
				adobeAnalytics.initAdobeAnalytics(
					userPreferences.emailAddress,
					!userPreferences.IsGuestUser,
					envConfig.adobeAnalytics.defaultDataLayer
				)
			).finally(() => {
				// ensures tracking is initialized before tracking the first page view
				setRoutes(getRoutes());
			});
		} else {
			setRoutes(getRoutes());
		}
	}, [userPreferences]);

	const getRoutes = () => {
		return createBrowserRouter(
			createRoutesFromElements(
				<Route
					element={<Layout hasNavigation={hasNavigation} hasError={hasError} />}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				>
					{env.allowDrydocks() && (
						<React.Fragment>
							<Route path="/drydock" element={<DryDock />} />
							<Route path="/spurs/drydock" element={<SpursDryDock />} />
							<Route path="/invdrydock" element={<InvinciblesDryDock />} />
							<Route path="/seahawksdrydock" element={<SeahawksDrydock />} />
						</React.Fragment>
					)}
					<Route
						path="/conclusion"
						element={trackRoute(Forms, pageName.conclusion, siteSection.forms)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
					<Route
						path="/profile"
						element={trackRoute(Forms, pageName.profile, siteSection.profile)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
					<Route
						path="/independence"
						element={trackRoute(IndependenceLoader, pageName.independence, siteSection.independence)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
					<Route
						path="/initProfile"
						element={trackRoute(ProfileLoader, pageName.profile, siteSection.profile)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
					<Route
						path="/canvasform"
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					>
						<Route index element={trackRoute(Forms, pageName.forms, siteSection.forms)} />
						<Route path=":view" element={trackRoute(Forms, pageName.view, siteSection.forms)} />
						<Route path="print" element={trackRoute(FormPrintViewWrapper, pageName.print, siteSection.forms)} />
						<Route path="changes" element={trackRoute(Forms, pageName.changes, siteSection.forms)} />
						<Route path="details" element={trackRoute(Forms, pageName.details, siteSection.forms)} />
					</Route>
					<Route
						path="/guidedworkflow"
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					>
						<Route index element={trackRoute(AresV1, pageName.guidedWorkFlow, siteSection.guidedWorkFlow)} />
						<Route path=":documentId" element={trackRoute(TaskView, pageName.document, siteSection.guidedWorkFlow)} />
						<Route
							path=":documentId/header/:headerId"
							element={trackRoute(TaskView, pageName.header, siteSection.guidedWorkFlow)}
						/>
						<Route
							path=":documentId/header/:headerId/section/:sectionId"
							element={trackRoute(TaskView, pageName.section, siteSection.guidedWorkFlow)}
						/>
						<Route
							path=":documentId/summary"
							element={trackRoute(TaskView, pageName.summary, siteSection.guidedWorkFlow)}
						/>
						<Route
							path="print"
							element={trackRoute(FormPrintViewWrapper, pageName.print, siteSection.guidedWorkFlow)}
						/>
						<Route
							path="configurations"
							element={trackRoute(HelixConfiguration, pageName.configurations, siteSection.guidedWorkFlow)}
						/>
					</Route>
					<Route
						path="/guidedworkflowv2"
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					>
						<Route index element={trackRoute(AresV2, pageName.guidedWorkFlowV2, siteSection.guidedWorkFlowV2)} />
						<Route
							path=":documentId"
							element={trackRoute(ExtendedV2Container, pageName.document, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path=":documentId/header/:headerId"
							element={trackRoute(ExtendedV2Container, pageName.header, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path=":documentId/header/:headerId/section/:sectionId"
							element={trackRoute(ExtendedV2Container, pageName.section, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path=":documentId/summary"
							element={trackRoute(GuidedWorkflowV2Container, pageName.summary, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path="print"
							element={trackRoute(FormPrintViewWrapper, pageName.print, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path="configurations"
							element={trackRoute(HelixConfiguration, pageName.configurations, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path="changes/:documentId"
							element={trackRoute(TrackChangesV2Container, pageName.changes, siteSection.guidedWorkFlowV2)}
						/>
						<Route
							path="compare/:documentId"
							element={trackRoute(CompareResponsesV2Container, pageName.compare, siteSection.compareDocument)}
						/>
						<Route
							path="compare/:documentId/header/:headerId/section/:sectionId"
							element={trackRoute(CompareResponsesV2DrillDownContainer, pageName.section, siteSection.compareDocument)}
						/>
						<Route
							path="attributes/:documentId"
							element={trackRoute(AttributesContainer, pageName.attributes, siteSection.attributes)}
						/>
					</Route>
					<Route
						path="/fit"
						element={trackRoute(FitRedirectionContainer, pageName.fit, siteSection.fit)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
					<Route
						path="/standalone"
						element={trackRoute(StandaloneContainer, pageName.standAlone, siteSection.standAlone)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					>
						<Route
							path=":documentId/header/:headerId/section/:sectionId/body/:bodyId"
							element={trackRoute(BodiesContainer, pageName.body, siteSection.standAlone)}
						/>
					</Route>
					<Route
						path="/cookiesettings"
						element={trackRoute(
							CookieSettings,
							pageName.cookieSettings,
							siteSection.consent,
							false,
							false,
							labels.cookieSettings
						)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
					<Route
						path="*"
						element={trackRoute(Forms, pageName.forms, siteSection.forms, true)}
						errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
					/>
				</Route>
			)
		);
	};

	const onCookieConsentResult = (type) => {
		dispatch(adobeAnalytics.setConsentResult(type));
	};

	return (
		<>
			{userPreferences.isPreferencesLoaded && routes ? <RouterProvider router={routes} /> : <LoaderContainer />}
			{displayCookieConsent && `${location.pathname}${location.search}` !== COOKIE_SETTINGS_PATH && (
				<CookieConsentModal
					settingsURL={COOKIE_SETTINGS_PATH}
					onResult={onCookieConsentResult}
					appName={labels.versionLabel}
					labels={{
						title: labels.aaCookieConsentTitle,
						prompt: labels.aaCookieContentPrompt,
						explanationHTML: labels.aaCookieConsentExplanation,
						explanationHTMLWithDoNotTrack: labels.aaCookieConsentExplanationWithDoNotTrack,
						declineOptionalAction: labels.aaCookieConsentDeclineOptionalAction,
						acceptAllAction: labels.aaCookieConsentAcceptAllAction,
						customizeAction: labels.aaCookieConsentCustomizeAction,
						customizeURL: labels.aaCookieConsentCustomizeURL
					}}
				/>
			)}
		</>
	);
}, isEqual);

App.displayName = 'App';

const StyledGuidedWorkflowBanners = styled.section`
	position: absolute;
	left: var(--px-60);
	width: calc(100% - var(--px-65));
	display: flex;
	flex-direction: column;
	z-index: 9;
	.bannerLabel.InsufficientRightsBannerLabel {
		width: calc(100vw - var(--px-160)) !important;
	}
`;

export default App;
